import { GENRE_NAMES } from '@mogzo/shared';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import type { GameCard } from '../../lib/games';
import { initials } from '../community/Avatar';

const DOTS = [
  ['10%', '24%', 10, '#7ad7f0'],
  ['32%', '62%', 7, '#f58ab0'],
  ['56%', '20%', 9, '#ffcb2f'],
  ['76%', '56%', 12, '#7ad7f0'],
  ['90%', '22%', 7, '#9be27a'],
] as const;

/** A game's cover, or MOGZO's dark dots where it has none. Decorative: its name sits beside it. */
export function GameCover({
  game,
  className = '',
  children,
}: {
  game: Pick<GameCard, 'coverUrl'>;
  className?: string;
  children?: ReactNode;
}) {
  return (
    <div className={`relative isolate overflow-hidden bg-ink ${className}`}>
      {game.coverUrl ? (
        <img src={game.coverUrl} alt="" className="absolute inset-0 size-full object-cover" />
      ) : (
        DOTS.map(([left, top, size, background]) => (
          <span
            key={left}
            aria-hidden="true"
            className="absolute rounded-full"
            style={{ left, top, width: size, height: size, background }}
          />
        ))
      )}
      {children}
    </div>
  );
}

/** A game's icon, or its initials on yellow. */
export function GameIcon({
  game,
  className = 'size-12 rounded-[13px] text-[17px]',
}: {
  game: Pick<GameCard, 'iconUrl' | 'name'>;
  className?: string;
}) {
  return game.iconUrl ? (
    <img src={game.iconUrl} alt="" className={`shrink-0 object-cover ${className}`} />
  ) : (
    <span
      aria-hidden="true"
      className={`flex shrink-0 items-center justify-center bg-brand font-display font-bold text-ink ${className}`}
    >
      {initials(game.name)}
    </span>
  );
}

export function NewChip() {
  return (
    <span className="inline-flex h-[22px] items-center rounded-full bg-brand px-2 text-[11px] font-bold tracking-[0.04em] text-ink uppercase">
      New
    </span>
  );
}

/** A game as a card: its cover, name, and a line under it (the genre unless `line` says). */
export function GameTile({
  game,
  line,
  className = '',
}: {
  game: GameCard;
  line?: ReactNode;
  className?: string;
}) {
  return (
    <Link to={game.path} className={`group block min-w-0 ${className}`}>
      <GameCover game={game} className="aspect-[4/3] rounded-[14px]" />
      <span className="mt-2 block truncate text-sm font-semibold group-hover:underline">
        {game.name}
      </span>
      <span className="block truncate text-xs leading-4 text-muted">
        {line ?? (game.genre ? GENRE_NAMES[game.genre] : `by ${game.developer || 'its developer'}`)}
      </span>
    </Link>
  );
}

/** A titled row of game cards that scrolls sideways on phones. */
export function GameRow({
  id,
  title,
  action,
  games,
}: {
  id: string;
  title: string;
  action?: ReactNode;
  games: GameCard[];
}) {
  return (
    <section aria-labelledby={id}>
      <div className="flex items-baseline justify-between gap-3 px-1">
        <h2 id={id} className="text-lg leading-6 font-bold tracking-[-0.01em]">
          {title}
        </h2>
        {action}
      </div>
      <ul className="mt-2.5 flex snap-x gap-3 overflow-x-auto pb-1 [scrollbar-width:none] sm:grid sm:grid-cols-3 sm:overflow-visible lg:grid-cols-4">
        {games.map((game) => (
          <li key={game.id} className="w-[40%] shrink-0 snap-start sm:w-auto">
            <GameTile game={game} />
          </li>
        ))}
      </ul>
    </section>
  );
}
